import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Building2, Mail, CheckCircle2, Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/password-reset/request", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="forgot-password-page">
        <Link
          to="/login/admin"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8"
          data-testid="forgot-back-to-login"
        >
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>

        <div className="h-11 w-11 rounded-md bg-slate-950 text-white flex items-center justify-center mb-6">
          <Building2 className="h-5 w-5" />
        </div>

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Rana Associates</p>
        <h2 className="mt-2 font-heading text-3xl font-bold text-slate-950">Forgot password?</h2>
        <p className="mt-2 text-slate-600 text-sm">
          Enter the email on your Rana Associates account and we'll send you a link to reset it.
        </p>

        {sent ? (
          <div
            className="mt-8 rounded-md border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
            data-testid="forgot-success"
          >
            <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-900">
              If an account exists for <b>{email}</b>, a reset link has been sent. It will expire in 30 minutes.
              Check your spam folder if you don't see it in your inbox.
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
            <div>
              <Label htmlFor="fp-email">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="fp-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-9"
                  data-testid="forgot-email-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-slate-950 hover:bg-slate-800 text-white h-11"
              data-testid="forgot-submit-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-slate-600">
          Remembered it?{" "}
          <Link to="/login/admin" className="text-blue-600 hover:underline font-medium" data-testid="forgot-goto-login">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
